import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Trash2, Plus, AlertTriangle, Car, FileText, Wrench, CircleDot } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { splitPersonName } from "@/lib/customerName";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  customer_number: "", customer_type: "organization", invoice_salutation: "company", name: "", first_name: "", last_name: "", email: "", phone: "", mobile_phone: "",
  preferred_contact: "auto", hu_reminders_enabled: false,
  vat_id: "", tax_treatment: "standard", payment_terms_days: 14, notes: "", is_active: true,
  datev_account: "",
  billing_line1: "", billing_line2: "", billing_postal_code: "", billing_city: "", billing_country: "DE",
  delivery_line1: "", delivery_line2: "", delivery_postal_code: "", delivery_city: "", delivery_country: "DE",
  contacts: [],
};

const EMPTY_VEHICLE = {
  registration_plate: "", vin: "", make: "", model: "", variant: "", first_registration: "",
  mileage: "", hsn: "", tsn: "", fuel_type: "", hu_due: "", au_due: "", owner_name: "",
};

export default function CustomerForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const { t, fmtDate, fmtCurrency } = useI18n();
  const { can, modules } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = !can("customers:write");
  const requestedReturnTo = searchParams.get("returnTo") || "";
  const returnTo = requestedReturnTo.startsWith("/") && !requestedReturnTo.startsWith("//") ? requestedReturnTo : "/customers";

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [createVehicle, setCreateVehicle] = useState(false);
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const namePartsTouched = useRef({ first_name: false, last_name: false });

  const { data, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => (await api.get(`/customers/${id}`)).data,
    enabled: isEdit,
  });
  const { data: numberSuggestion } = useQuery({
    queryKey: ["customer-number-suggestion"],
    queryFn: async () => (await api.get("/customers/number-suggestion")).data,
    enabled: !isEdit,
  });
  const { data: numberCheck } = useQuery({
    queryKey: ["customer-number-check", form.customer_number],
    queryFn: async () => (await api.get("/customers/check-number", { params: { customer_number: form.customer_number } })).data,
    enabled: !isEdit && !!form.customer_number?.trim(),
  });
  const { data: dossier } = useQuery({ queryKey: ["automotive-dossier", id], enabled: isEdit && modules?.includes("automotive_workshop"), queryFn: async () => (await api.get(`/automotive/customers/${id}/dossier`)).data });

  useEffect(() => {
    if (data) {
      setForm({ ...EMPTY, ...data, contacts: data.contacts || [] });
      namePartsTouched.current = { first_name: true, last_name: true };
    }
  }, [data]);
  useEffect(() => {
    if (!isEdit && numberSuggestion?.customer_number) {
      setForm((current) => current.customer_number ? current : { ...current, customer_number: numberSuggestion.customer_number });
    }
  }, [isEdit, numberSuggestion]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setCustomerType = (customerType) => {
    setForm((current) => {
      if (customerType !== "person") return { ...current, customer_type: customerType, invoice_salutation: "company" };
      const parsed = splitPersonName(current.name);
      return {
        ...current, customer_type: customerType,
        invoice_salutation: ["mr", "ms", "family"].includes(current.invoice_salutation) ? current.invoice_salutation : "neutral",
        first_name: namePartsTouched.current.first_name ? current.first_name : parsed.first_name,
        last_name: namePartsTouched.current.last_name ? current.last_name : parsed.last_name,
      };
    });
    setDirty(true);
  };
  const setCustomerName = (name) => {
    setForm((current) => {
      if (current.customer_type !== "person") return { ...current, name };
      const parsed = splitPersonName(name);
      return {
        ...current, name,
        first_name: namePartsTouched.current.first_name ? current.first_name : parsed.first_name,
        last_name: namePartsTouched.current.last_name ? current.last_name : parsed.last_name,
      };
    });
    setDirty(true);
  };
  const setNamePart = (key, value) => {
    namePartsTouched.current[key] = true;
    set(key, value);
  };

  const checkDupe = useCallback(async () => {
    if (isEdit || !form.name) return;
    try {
      const { data: r } = await api.get("/customers/check-duplicate", { params: { name: form.name, email: form.email, vat_id: form.vat_id } });
      setDupes(r.duplicates || []);
    } catch (e) { console.warn("duplicate check failed", e); }
  }, [form.name, form.email, form.vat_id, isEdit]);

  const addContact = () => set("contacts", [...form.contacts, { name: "", position: "", email: "", phone: "" }]);
  const setContact = (i, k, v) => {
    const next = form.contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c));
    set("contacts", next);
  };
  const removeContact = (i) => set("contacts", form.contacts.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, payment_terms_days: Number(form.payment_terms_days) || 0,
        vehicle: !isEdit && createVehicle ? {
          ...vehicle, mileage: vehicle.mileage || null,
          first_registration: vehicle.first_registration || null,
          hu_due: vehicle.hu_due || null, au_due: vehicle.au_due || null,
        } : null,
      };
      if (isEdit) {
        await api.put(`/customers/${id}`, payload);
        toast.success(t("customers.updated_success"));
      } else {
        await api.post("/customers", payload);
        toast.success(t("customers.created_success"));
      }
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["automotive-vehicles"] });
      navigate(returnTo);
    } catch (err) {
      toast.error(apiErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${id}`);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["automotive-vehicles"] });
      toast.success(t("customers.deleted_success"));
      navigate("/customers");
    } catch (error) {
      toast.error(apiErrorMessage(error, t));
      setDeleteOpen(false);
    } finally { setDeleting(false); }
  };

  if (isEdit && isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const isOrg = form.customer_type === "organization";
  const numberUnavailable = !isEdit && !!numberCheck && !numberCheck.available;

  return (
    <div>
      <PageHeader
        title={isEdit ? t("customers.edit") : t("customers.new")}
        actions={<div className="flex gap-2">{isEdit && !readOnly && <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setDeleteOpen(true)} data-testid="customer-delete"><Trash2 className="h-4 w-4" />{t("common.delete")}</Button>}<Button variant="outline" onClick={() => navigate(returnTo)} data-testid="customer-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>{!readOnly && <Button type="submit" form="customer-form" disabled={saving || numberUnavailable} data-testid="customer-save-top">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("common.save")}</Button>}</div>}
      />

      <form id="customer-form" onSubmit={submit} className="space-y-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("customers.type")}</Label>
              <Select value={form.customer_type} onValueChange={setCustomerType} disabled={readOnly}>
                <SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">{t("customers.organization")}</SelectItem>
                  <SelectItem value="person">{t("customers.person")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.number")}</Label>
              <Input required value={form.customer_number || ""} onChange={(event) => set("customer_number", event.target.value)} disabled={isEdit || readOnly} data-testid="customer-number-input" />
              {!isEdit && form.customer_number && <p className={`text-xs ${numberCheck?.available ? "text-emerald-600" : "text-amber-600"}`}>{numberCheck ? (numberCheck.available ? t("customers.number_available") : t("customers.number_already_used")) : t("common.loading")}</p>}
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("customers.invoice_salutation")}</Label>
              <Select value={form.invoice_salutation || "auto"} onValueChange={(value) => set("invoice_salutation", value)} disabled={readOnly}>
                <SelectTrigger data-testid="customer-invoice-salutation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mr">{t("customers.salutation_mr")}</SelectItem>
                  <SelectItem value="ms">{t("customers.salutation_ms")}</SelectItem>
                  <SelectItem value="family">{t("customers.salutation_family")}</SelectItem>
                  <SelectItem value="company">{t("customers.salutation_company")}</SelectItem>
                  <SelectItem value="neutral">{t("customers.salutation_neutral")}</SelectItem>
                  <SelectItem value="auto">{t("customers.salutation_auto")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t("customers.invoice_salutation_hint")}</p>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("customers.company_name")} *</Label>
              <Input required value={form.name} onChange={(e) => setCustomerName(e.target.value)} onBlur={checkDupe} disabled={readOnly} data-testid="customer-name-input" />
            </div>
            {!isOrg && (
              <>
                <div className="space-y-1.5">
                  <Label>{t("customers.first_name")}</Label>
                  <Input value={form.first_name || ""} onChange={(e) => setNamePart("first_name", e.target.value)} disabled={readOnly} data-testid="customer-first-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("customers.last_name")}</Label>
                  <Input value={form.last_name || ""} onChange={(e) => setNamePart("last_name", e.target.value)} disabled={readOnly} data-testid="customer-last-name-input" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>{t("common.email")}</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} disabled={readOnly} data-testid="customer-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.phone")}</Label>
              <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.mobile_phone")}</Label>
              <Input type="tel" value={form.mobile_phone || ""} onChange={(e) => set("mobile_phone", e.target.value)} disabled={readOnly} placeholder="+49 …" data-testid="customer-mobile-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.preferred_contact")}</Label>
              <Select value={form.preferred_contact || "auto"} onValueChange={(v) => set("preferred_contact", v)} disabled={readOnly}>
                <SelectTrigger data-testid="customer-contact-preference"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{t("customers.contact_auto")}</SelectItem>
                  <SelectItem value="email">E-Mail</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="phone">{t("customers.contact_phone")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6 md:col-span-2">
              <Switch checked={!!form.hu_reminders_enabled} onCheckedChange={(v) => set("hu_reminders_enabled", v)} disabled={readOnly} id="hu-reminders" data-testid="customer-hu-reminders-switch" />
              <div><Label htmlFor="hu-reminders">{t("customers.hu_reminders_enabled")}</Label><p className="text-xs text-slate-500">{t("customers.hu_reminders_hint")}</p></div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.vat_id")}</Label>
              <Input value={form.vat_id || ""} onChange={(e) => set("vat_id", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.tax_treatment")}</Label>
              <Select value={form.tax_treatment} onValueChange={(v) => set("tax_treatment", v)} disabled={readOnly}>
                <SelectTrigger data-testid="customer-tax-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">{t("customers.tax_standard")}</SelectItem>
                  <SelectItem value="reverse_charge">{t("customers.tax_reverse_charge")}</SelectItem>
                  <SelectItem value="small_business">{t("customers.tax_small_business")}</SelectItem>
                  <SelectItem value="exempt">{t("customers.tax_exempt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.payment_terms")}</Label>
              <Input type="number" min="0" value={form.payment_terms_days} onChange={(e) => set("payment_terms_days", e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.datev_account")}</Label>
              <Input inputMode="numeric" value={form.datev_account || ""} onChange={(e) => set("datev_account", e.target.value.replace(/\D/g, "").slice(0, 9))} disabled={readOnly} placeholder={t("customers.datev_account_hint")} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} disabled={readOnly} data-testid="customer-active-switch" id="active" />
              <Label htmlFor="active">{t("common.active")}</Label>
            </div>
          </div>

          {dupes.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" />{t("customers.duplicate_warning")}</div>
              <ul className="mt-1 list-inside list-disc">
                {dupes.map((d) => <li key={d.id}>{d.name} ({d.customer_number})</li>)}
              </ul>
            </div>
          )}
        </Card>

        {!isEdit && modules?.includes("automotive_workshop") && <Card className="p-6" data-testid="customer-create-vehicle-card">
          <div className="flex items-center justify-between gap-4"><div><h3 className="flex items-center gap-2 font-semibold"><Car className="h-4 w-4" />{t("customers.create_vehicle_together")}</h3><p className="mt-1 text-sm text-slate-500">{t("customers.create_vehicle_together_hint")}</p></div><Switch checked={createVehicle} onCheckedChange={setCreateVehicle} id="create-vehicle-together" /></div>
          {createVehicle && <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("automotive.plate")} *</Label><Input required value={vehicle.registration_plate} onChange={(event) => setVehicle((current) => ({ ...current, registration_plate: event.target.value.toUpperCase() }))} /></div>
            <div className="space-y-1.5"><Label>FIN / VIN</Label><Input value={vehicle.vin} maxLength={17} onChange={(event) => setVehicle((current) => ({ ...current, vin: event.target.value.toUpperCase() }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.make")} *</Label><Input required value={vehicle.make} onChange={(event) => setVehicle((current) => ({ ...current, make: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.model")} *</Label><Input required value={vehicle.model} onChange={(event) => setVehicle((current) => ({ ...current, model: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.variant")}</Label><Input value={vehicle.variant} onChange={(event) => setVehicle((current) => ({ ...current, variant: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.first_registration")}</Label><Input type="date" value={vehicle.first_registration} onChange={(event) => setVehicle((current) => ({ ...current, first_registration: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.mileage")}</Label><Input type="number" min="0" value={vehicle.mileage} onChange={(event) => setVehicle((current) => ({ ...current, mileage: event.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-2"><div className="space-y-1.5"><Label>HSN</Label><Input value={vehicle.hsn} onChange={(event) => setVehicle((current) => ({ ...current, hsn: event.target.value.toUpperCase() }))} /></div><div className="space-y-1.5"><Label>TSN</Label><Input value={vehicle.tsn} onChange={(event) => setVehicle((current) => ({ ...current, tsn: event.target.value.toUpperCase() }))} /></div></div>
            <div className="space-y-1.5"><Label>{t("automotive.hu_due")}</Label><Input type="date" value={vehicle.hu_due} onChange={(event) => setVehicle((current) => ({ ...current, hu_due: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.au_due")}</Label><Input type="date" value={vehicle.au_due} onChange={(event) => setVehicle((current) => ({ ...current, au_due: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.fuel")}</Label><Input value={vehicle.fuel_type} onChange={(event) => setVehicle((current) => ({ ...current, fuel_type: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("automotive.owner")}</Label><Input value={vehicle.owner_name} onChange={(event) => setVehicle((current) => ({ ...current, owner_name: event.target.value }))} /></div>
          </div>}
        </Card>}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {["billing", "delivery"].map((kind) => (
            <Card key={kind} className="p-6">
              <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t(`customers.${kind}_address`)}</h3>
              <div className="space-y-3">
                <Input placeholder={t("customers.line1")} value={form[`${kind}_line1`] || ""} onChange={(e) => set(`${kind}_line1`, e.target.value)} disabled={readOnly} data-testid={`${kind}-line1`} />
                <Input placeholder={t("customers.line2")} value={form[`${kind}_line2`] || ""} onChange={(e) => set(`${kind}_line2`, e.target.value)} disabled={readOnly} />
                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder={t("customers.postal_code")} value={form[`${kind}_postal_code`] || ""} onChange={(e) => set(`${kind}_postal_code`, e.target.value)} disabled={readOnly} />
                  <Input className="col-span-2" placeholder={t("customers.city")} value={form[`${kind}_city`] || ""} onChange={(e) => set(`${kind}_city`, e.target.value)} disabled={readOnly} />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("customers.contacts")}</h3>
            {!readOnly && <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-button"><Plus className="h-4 w-4" />{t("customers.add_contact")}</Button>}
          </div>
          {form.contacts.length === 0 ? (
            <p className="text-sm text-slate-400">{t("common.none")}</p>
          ) : (
            <div className="space-y-3">
              {form.contacts.map((c, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]" data-testid={`contact-row-${i}`}>
                  <Input placeholder={t("common.name")} value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} disabled={readOnly} />
                  <Input placeholder={t("customers.position")} value={c.position || ""} onChange={(e) => setContact(i, "position", e.target.value)} disabled={readOnly} />
                  <Input placeholder={t("common.email")} value={c.email || ""} onChange={(e) => setContact(i, "email", e.target.value)} disabled={readOnly} />
                  <Input placeholder={t("common.phone")} value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} disabled={readOnly} />
                  {!readOnly && <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {isEdit && modules?.includes("automotive_workshop") && <Card className="p-6"><div className="mb-4 flex items-center justify-between"><div><h3 className="font-semibold">{t("automotive.customer_dossier")}</h3><p className="text-sm text-slate-500">{t("automotive.customer_dossier_hint")}</p></div>{can("customers:write") && <Button type="button" variant="outline" onClick={() => navigate("/vehicles")}><Plus className="h-4 w-4" />{t("automotive.new_vehicle")}</Button>}</div><div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border p-4"><h4 className="mb-2 flex items-center gap-2 font-medium"><Car className="h-4 w-4" />{t("automotive.title")}</h4>{dossier?.vehicles?.length ? dossier.vehicles.map((v) => <button type="button" key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} className="flex w-full justify-between border-b py-2 text-left text-sm last:border-0"><span><b>{v.registration_plate}</b> · {v.make} {v.model}</span><span className="text-slate-500">HU {fmtDate(v.hu_due) || "—"}</span></button>) : <p className="text-sm text-slate-500">{t("automotive.no_vehicles")}</p>}</div>
          <div className="rounded-md border p-4"><h4 className="mb-2 flex items-center justify-between gap-2 font-medium"><span className="flex items-center gap-2"><FileText className="h-4 w-4" />{t("automotive.customer_invoices")}</span>{dossier?.invoice_count > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">{dossier.invoice_count}</span>}</h4>{dossier?.invoices?.length ? <div className="max-h-96 overflow-y-auto pr-1">{dossier.invoices.map((x) => <button type="button" key={x.id} onClick={() => navigate(`/finance/einvoices/${x.id}?returnTo=${encodeURIComponent(`/customers/${id}`)}`)} className="flex w-full items-center justify-between gap-3 border-b py-2 text-left text-sm last:border-0"><span><b>{x.number || t("common.draft")}</b>{x.status === "historical" && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">{t("einvoice.status.historical")}</span>}<span className="mt-0.5 block text-xs text-slate-500">{x.vehicle?.registration_plate || x.vehicle?.legacy_description || ""}</span></span><span className="shrink-0 text-right"><span>{fmtCurrency(x.total)}</span><span className="block text-xs text-slate-500">{fmtDate(x.issue_date)}</span></span></button>)}</div> : <p className="text-sm text-slate-500">{t("automotive.no_invoices")}</p>}</div>
          <div className="rounded-md border p-4"><h4 className="mb-2 flex items-center gap-2 font-medium"><Wrench className="h-4 w-4" />{t("automotive.jobs")}</h4>{dossier?.jobs?.length ? dossier.jobs.slice(0, 8).map((x) => <button type="button" key={x.id} onClick={() => navigate(`/jobs?open=${x.id}`)} className="flex w-full justify-between border-b py-2 text-left text-sm last:border-0"><span>{x.number} · {x.title}</span><span>{x.status}</span></button>) : <p className="text-sm text-slate-500">{t("automotive.no_history")}</p>}</div>
          <div className="rounded-md border p-4"><h4 className="mb-2 flex items-center gap-2 font-medium"><CircleDot className="h-4 w-4" />{t("automotive.tire_sets")}</h4><p className="text-2xl font-bold">{dossier?.tire_sets?.filter((x) => x.status !== "released").length || 0}</p><Button type="button" variant="link" className="h-auto p-0" onClick={() => navigate("/tire-storage")}>{t("automotive.open_tire_storage")}</Button></div>
        </div></Card>}

        <div className="space-y-1.5">
          <Label>{t("common.notes")}</Label>
          <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} disabled={readOnly} rows={3} />
        </div>

        {!readOnly && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(returnTo)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving || numberUnavailable} data-testid="customer-save-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("common.save")}
            </Button>
          </div>
        )}
      </form>
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t("customers.delete_title")}</AlertDialogTitle><AlertDialogDescription>{t("customers.delete_hint")}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" disabled={deleting} onClick={deleteCustomer}>{t("common.delete")}</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
